/* ==========================================================================
   avella-flow-carousel.js
   Mobile-only autoplay for the existing scroll-snap carousels.

   Attaches to any element carrying [data-avf-autoplay]. It drives the
   element's own scrollLeft, so the existing markup, CSS, snap points and
   manual swiping all keep working exactly as they did — nothing is cloned,
   wrapped or re-laid-out, so there is no overflow or layout shift.
   ========================================================================== */

(function () {
  'use strict';

  var AUTO_MS = 3500;   // dwell on each card
  var SLIDE_MS = 420;   // one-card advance
  var WRAP_MS = 560;    // last -> first rewind (further to travel)
  var RESUME_MS = 4000; // idle wait after the user interacts
  var MOBILE = '(max-width: 749px)';

  var mqMobile = window.matchMedia(MOBILE);
  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  var cars = [];
  var raf = 0;
  var io = null;
  var ro = null;

  function now() {
    return window.performance && performance.now ? performance.now() : Date.now();
  }

  /* ---------- geometry ---------- */

  function cards(c) {
    return Array.prototype.slice.call(c.el.children).filter(function (n) {
      return n.nodeType === 1;
    });
  }

  // Snap offsets, measured live. Cleared on resize so orientation changes
  // and late font loading can't leave us aiming at stale positions.
  function snaps(c) {
    if (c.snaps) return c.snaps;
    var list = cards(c);
    if (!list.length) { c.snaps = []; return c.snaps; }
    var box = c.el.getBoundingClientRect();
    var left = c.el.scrollLeft;
    var cw = c.el.clientWidth;
    var max = Math.max(0, c.el.scrollWidth - cw);
    c.snaps = list.map(function (card) {
      var r = card.getBoundingClientRect();
      // centre the card, then clamp so the first and last land flush
      var target = left + (r.left - box.left) - (cw - r.width) / 2;
      return Math.max(0, Math.min(max, Math.round(target)));
    });
    return c.snaps;
  }

  function nearest(c) {
    var s = snaps(c);
    if (!s.length) return 0;
    var x = c.el.scrollLeft;
    var best = 0;
    var bd = Infinity;
    for (var i = 0; i < s.length; i++) {
      var d = Math.abs(s[i] - x);
      if (d < bd) { bd = d; best = i; }
    }
    return best;
  }

  /* ---------- dots ---------- */

  function paintDots(c, i) {
    if (!c.dots || !c.dots.length) return;
    for (var d = 0; d < c.dots.length; d++) {
      c.dots[d].setAttribute('aria-selected', d === i ? 'true' : 'false');
    }
  }

  function syncIndex(c) {
    var i = nearest(c);
    if (i === c.index) return;
    c.index = i;
    paintDots(c, i);
  }

  /* ---------- playback gating ---------- */

  // Only a genuinely overflowing scroller counts. On desktop the same element
  // is a CSS grid with no overflow, so this is false and autoplay never runs.
  function scrollable(c) {
    return c.el.scrollWidth - c.el.clientWidth > 8;
  }

  function plays(c) {
    return mqMobile.matches &&
      !mqReduce.matches &&
      !document.hidden &&
      c.visible &&
      !c.held &&
      scrollable(c);
  }

  function cancel(c) {
    if (!c.anim) return;
    c.anim = null;
    c.el.style.scrollSnapType = '';
    c.settleUntil = now() + 140;
  }

  /* ---------- movement ---------- */

  function goto(c, i, dur) {
    var s = snaps(c);
    if (!s.length) return;
    i = Math.max(0, Math.min(s.length - 1, i));
    var to = s[i];
    var from = c.el.scrollLeft;
    var t = now();

    c.index = i;
    paintDots(c, i);

    if (!dur || Math.abs(to - from) < 1.5 || mqReduce.matches) {
      c.el.scrollLeft = to;
      c.settleUntil = t + 200;
      c.playAt = t + AUTO_MS;
      return;
    }

    // Snap must be off while we tween or the browser fights the animation.
    c.el.style.scrollSnapType = 'none';
    c.anim = { from: from, delta: to - from, to: to, t0: t, dur: dur };
    c.settleUntil = t + dur + 200;
    kick();
  }

  function advance(c) {
    var n = snaps(c).length;
    if (n < 2) { c.playAt = now() + AUTO_MS; return; }
    var i = (c.index + 1) % n;
    goto(c, i, i === 0 ? WRAP_MS : SLIDE_MS);
  }

  function kick() {
    if (raf) return;
    var live = cars.some(function (c) { return c.anim || plays(c); });
    if (!live) return;
    raf = requestAnimationFrame(tick);
  }

  function tick(t) {
    raf = 0;
    var live = false;

    for (var i = 0; i < cars.length; i++) {
      var c = cars[i];

      if (c.anim) {
        live = true;
        var a = c.anim;
        var p = Math.min(1, (t - a.t0) / a.dur);
        // easeInOutCubic
        var e = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
        c.el.scrollLeft = a.from + a.delta * e;
        if (p >= 1) {
          c.el.scrollLeft = a.to;
          c.el.style.scrollSnapType = '';
          c.anim = null;
          c.settleUntil = t + 220;
          c.playAt = t + AUTO_MS;
        }
      } else if (plays(c)) {
        live = true;
        // A backgrounded tab can leave playAt far in the past; don't burst.
        if (t - c.playAt > 3000) c.playAt = t + AUTO_MS;
        else if (t >= c.playAt) advance(c);
      }
    }

    if (live && !raf) raf = requestAnimationFrame(tick);
  }

  /* ---------- wiring ---------- */

  function attach(el) {
    if (!el || el.dataset.avfCarouselReady) return;
    el.dataset.avfCarouselReady = '1';

    var section = el.closest('.shopify-section') || document;
    var c = {
      el: el,
      dots: Array.prototype.slice.call(section.querySelectorAll('[data-avf-dot]')),
      index: 0,
      snaps: null,
      anim: null,
      held: false,
      visible: true,
      settleUntil: 0,
      rafSync: 0,
      playAt: now() + AUTO_MS
    };
    cars.push(c);

    function hold() {
      c.held = true;
      cancel(c);
    }
    function release() {
      c.held = false;
      c.playAt = now() + RESUME_MS;
      kick();
    }

    el.addEventListener('pointerdown', hold, { passive: true });
    el.addEventListener('touchstart', hold, { passive: true });
    ['pointerup', 'pointercancel', 'touchend', 'touchcancel'].forEach(function (ev) {
      el.addEventListener(ev, release, { passive: true });
    });
    el.addEventListener('wheel', function () {
      cancel(c);
      c.playAt = now() + RESUME_MS;
      kick();
    }, { passive: true });
    el.addEventListener('focusin', hold);
    el.addEventListener('focusout', function (e) {
      if (!el.contains(e.relatedTarget)) release();
    });

    // Keep dots honest during manual swiping, but ignore the scroll events
    // our own tween produces.
    el.addEventListener('scroll', function () {
      var t = now();
      if (c.anim || t < c.settleUntil) return;
      c.playAt = t + RESUME_MS;
      if (!c.rafSync) {
        c.rafSync = requestAnimationFrame(function () {
          c.rafSync = 0;
          syncIndex(c);
        });
      }
      kick();
    }, { passive: true });

    // Dots drive the carousel and count as interaction.
    c.dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        cancel(c);
        goto(c, i, SLIDE_MS);
        c.playAt = now() + RESUME_MS;
        kick();
      });
    });

    if (io) io.observe(el);
    if (ro) ro.observe(el);
  }

  function boot() {
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var c = cars.filter(function (x) { return x.el === en.target; })[0];
          if (!c) return;
          c.visible = en.intersectionRatio >= 0.4;
          if (!c.visible) cancel(c);
          else c.playAt = Math.max(c.playAt, now() + 1200);
        });
        kick();
      }, { threshold: [0, 0.4] });
    }

    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(function () {
        cars.forEach(function (c) { c.snaps = null; });
      });
    }

    Array.prototype.forEach.call(document.querySelectorAll('[data-avf-autoplay]'), attach);

    var remeasure = function () {
      cars.forEach(function (c) {
        c.snaps = null;
        cancel(c);
        c.playAt = now() + AUTO_MS;
      });
      kick();
    };

    if (mqMobile.addEventListener) {
      mqMobile.addEventListener('change', remeasure);
      mqReduce.addEventListener('change', remeasure);
    }
    window.addEventListener('orientationchange', remeasure);
    window.addEventListener('pageshow', remeasure);
    window.addEventListener('load', remeasure);

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) cars.forEach(cancel);
      else {
        cars.forEach(function (c) { c.playAt = now() + 1200; });
        kick();
      }
    });

    kick();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The theme editor re-renders a section's DOM without reloading the page.
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('[data-avf-autoplay]');
    if (el) attach(el);
  });
})();
